import { Button } from '@/components/ui/button';
import { useApi } from '@/hooks/useApi';
import { useAuthStore } from '@/stores/authStore';
import { AlertCircle, CheckCircle, Clock, CreditCard, Sparkles, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';

const DISMISSED_KEY = 'ergshoot_subscribe_banner_dismissed';

interface BannerCopy {
  title: string;
  body: string;
  cta?: string;
  icon: React.ReactNode;
  tone: 'pending' | 'pending_approval' | 'past_due' | 'cancelled' | 'paused' | 'gym_not_active';
}

function getCopy(
  status: string | undefined,
  source: 'self' | 'gym' | 'bypass' | undefined,
  billingState: 'pending_approval' | null | undefined
): BannerCopy | null {
  if (billingState === 'pending_approval') {
    return {
      title: 'Payment received — application under review',
      body: 'HQ is reviewing your affiliate application. No further action is required.',
      icon: <CheckCircle className="h-5 w-5" />,
      tone: 'pending_approval',
    };
  }

  // Members/coaches inheriting a frozen gym → D3 "Coming Soon" variant
  if (source === 'gym') {
    return {
      title: 'Your gym is finalising its setup',
      body: "Your trainer will activate your access shortly. Explore the platform in the meantime, and give your gym a nudge if you're ready to get started.",
      cta: 'Nudge your gym',
      icon: <Clock className="h-5 w-5" />,
      tone: 'gym_not_active',
    };
  }

  switch (status) {
    case 'pending':
      return {
        title: "You're exploring Ergshoot for free",
        body: 'Subscribe to log scores, join leaderboards, register for events, and unlock everything Ergshoot has to offer.',
        cta: 'Subscribe to unlock',
        icon: <Sparkles className="h-5 w-5" />,
        tone: 'pending',
      };
    case 'past_due':
      return {
        title: 'Your payment failed',
        body: 'Update your card to avoid losing access to premium features.',
        cta: 'Update payment method',
        icon: <CreditCard className="h-5 w-5" />,
        tone: 'past_due',
      };
    case 'cancelled':
      return {
        title: 'Your subscription has ended',
        body: 'Resubscribe to restore full access. Your data is safe.',
        cta: 'Resubscribe',
        icon: <AlertCircle className="h-5 w-5" />,
        tone: 'cancelled',
      };
    case 'paused':
      return {
        title: 'Your subscription is paused',
        body: 'Resume your subscription any time to get straight back to training.',
        cta: 'Resume now',
        icon: <Clock className="h-5 w-5" />,
        tone: 'paused',
      };
    default:
      return null;
  }
}

/**
 * FEAT-APR08-001: persistent dashboard banner shown to frozen freemium users.
 *
 * State-aware copy:
 *   pending          → "Explore for free, subscribe to unlock"
 *   past_due         → "Your payment failed, update card"
 *   cancelled        → "Resubscribe to restore access"
 *   paused           → "Resume now"
 *   gym_not_active   → "Coming Soon — your gym is finalising setup" (D3)
 *                       For members/coaches of a frozen owner. CTA is
 *                       "Nudge your gym" which calls a backend endpoint
 *                       that emails the gym owner.
 *
 * Dismissable per session (sessionStorage), not permanently.
 */
export function SubscribeBanner() {
  const { api } = useApi();
  const { pathname } = useLocation();
  const user = useAuthStore((s) => s.user);
  const [dismissed, setDismissed] = useState(() => {
    if (typeof window === 'undefined') return false;
    return sessionStorage.getItem(DISMISSED_KEY) === '1';
  });
  const [nudging, setNudging] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (dismissed) {
      sessionStorage.setItem(DISMISSED_KEY, '1');
    }
  }, [dismissed]);

  if (!user?.is_frozen || pathname === '/affiliate/billing' || pathname === '/individual/billing') {
    return null;
  }

  const copy = getCopy(user.subscription_status, user.subscription_source, user.billing_state);
  if (!copy) return null;

  // past_due is persistent per AC — cannot be dismissed.
  const dismissable = copy.tone !== 'past_due';
  if (dismissed && dismissable) return null;

  const handleCta = async () => {
    if (copy.tone === 'pending_approval') return;

    if (copy.tone === 'gym_not_active') {
      // D3: nudge the gym owner via email
      setNudging(true);
      try {
        const res = await api.post<{ message: string }>('/members/nudge-owner', {});
        if (res.success) {
          toast.success(
            res.data?.message || "Your trainer has been notified — they'll get you set up soon."
          );
        } else {
          toast.error(res.error || 'Could not send the nudge. Please try again.');
        }
      } catch {
        toast.error('Could not send the nudge. Please try again.');
      } finally {
        setNudging(false);
      }
      return;
    }

    // past_due / paused → open Stripe Customer Portal (update card / resume)
    // pending / cancelled → open Stripe Checkout (new subscription)
    if (copy.tone === 'past_due' || copy.tone === 'paused') {
      const portalPath =
        user.role === 'individual' ? '/individuals/billing/portal' : '/affiliates/billing/portal';
      try {
        const res = await api.post<{ url: string }>(portalPath, {});
        if (res.success && res.data?.url) {
          window.location.href = res.data.url;
        } else {
          toast.error(res.error || 'Could not open billing portal. Please try again.');
        }
      } catch {
        toast.error('Could not open billing portal. Please try again.');
      }
      return;
    }

    // Pending affiliate owners must pay through the application flow so the
    // resulting Stripe session is attached to the HQ-reviewable application.
    if (copy.tone === 'pending' && user.role === 'affiliate_owner') {
      window.location.href = '/apply/affiliate';
      return;
    }

    // The user already has a D1 role at this point, so the signup-only auth
    // checkout rejects them. Use the role-owned billing endpoint instead.
    const checkoutPath =
      user.role === 'individual' ? '/individuals/billing/checkout' : '/affiliates/billing/checkout';
    try {
      const res = await api.post<{ checkout_url: string }>(checkoutPath, {});
      if (res.success && res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      } else {
        toast.error(res.error || 'Could not start checkout. Please try again.');
      }
    } catch {
      toast.error('Could not start checkout. Please try again.');
    }
  };

  const toneClasses = {
    pending: 'border-primary/40 bg-primary/10',
    pending_approval: 'border-green-500/40 bg-green-500/10',
    past_due: 'border-red-500/50 bg-red-500/15',
    cancelled: 'border-red-500/40 bg-red-500/10',
    paused: 'border-blue-500/40 bg-blue-500/10',
    gym_not_active: 'border-primary/40 bg-primary/10',
  } as const;

  return (
    <div
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${toneClasses[copy.tone]}`}
    >
      <div className="shrink-0 text-primary">{copy.icon}</div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm text-foreground">{copy.title}</p>
        <p className="text-xs text-muted-foreground">{copy.body}</p>
      </div>
      {copy.cta && (
        <Button size="sm" onClick={handleCta} disabled={nudging} className="shrink-0">
          {nudging ? 'Sending…' : copy.cta}
        </Button>
      )}
      {dismissable && (
        <button
          type="button"
          onClick={() => setDismissed(true)}
          className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
          aria-label="Dismiss banner for this session"
        >
          <X className="h-4 w-4" />
        </button>
      )}
    </div>
  );
}
